// Значката „⇄" за реда на продукт в „Наличности": „⇄ заместил Кафе A · 3" или
// „⇄ заместен с Кафе · 3" за периода; при повече другари — „Кафе A +1".
// Подсказката изрежда всички замени с количествата. Самостоятелна: данните
// идват от `useStockSubstitutionSummary` (една заявка за цялата справка).
// `compact` — само „⇄ 3" (тесният списък до складовата карта).
import { Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { formatQty } from "../../components/qty";
import { formatPeriod, type Period } from "../../components/period/periodPresets";
import { badgeViewOf, type ProductSubstitutionSummary } from "./stockSubstitutionModel";

type Props = {
  summary: ProductSubstitutionSummary | undefined;
  period: Period;
  decimals: number; // знаците на мярката на продукта от реда
  compact?: boolean;
};

export const StockSubstitutionBadge = ({ summary, period, decimals, compact = false }: Props) => {
  const { t } = useTranslation();
  const view = badgeViewOf(summary);
  if (!view || !summary) return null;

  const unit = summary.unit ? ` ${summary.unit}` : "";
  const lines = [
    ...summary.replaced.map((item) =>
      t("inventoryPage.scBadgeTipReplaced", {
        name: item.name,
        count: item.count,
        qty: `${formatQty(item.quantity, decimals)}${unit}`,
      }),
    ),
    ...summary.replacedBy.map((item) =>
      t("inventoryPage.scBadgeTipReplacedBy", {
        name: item.name,
        count: item.count,
        qty: `${formatQty(item.quantity, decimals)}${unit}`,
      }),
    ),
  ];
  const label = t(
    view.role === "replaced" ? "inventoryPage.scBadgeReplaced" : "inventoryPage.scBadgeReplacedBy",
    { name: view.name },
  );
  const tooltip = (
    <div className="flex flex-col gap-0.5 text-[12.5px] leading-[1.4]">
      <span className="font-semibold">
        {t("inventoryPage.scBadgeTipTitle", { period: formatPeriod(period) })}
      </span>
      {lines.map((line) => (
        <span key={line}>{line}</span>
      ))}
    </div>
  );

  return (
    <Tooltip title={tooltip} placement="top">
      <span
        aria-label={[label, ...lines].join(". ")}
        className="inline-flex min-w-0 max-w-full shrink items-center gap-1 whitespace-nowrap rounded-full bg-brand-tint px-2 py-[2px] text-[11.5px] font-medium leading-[normal] text-brand-deep-ink"
      >
        {compact ? (
          <span aria-hidden>⇄</span>
        ) : (
          <span className="min-w-0 truncate">
            {label}
            {view.others > 0 && ` +${view.others}`}
          </span>
        )}
        <span aria-hidden className="font-sheet">
          {compact ? view.count : `· ${view.count}`}
        </span>
      </span>
    </Tooltip>
  );
};

export default StockSubstitutionBadge;
